import { useEffect, useState } from 'react';

/**
 * Ou tourne l instance qui a servi CETTE page (lot feat-le-bandeau-de-preproduction).
 * `unknown` tant que rien ne l a dit, ou quand la reponse est illisible.
 */
export type GsDeploymentSlotState = 'preprod' | 'production' | 'unknown';

/**
 * Ce que `/api/version` dit de la construction qui sert la page (lot
 * feat-la-version-se-voit-partout). Une valeur absente ou illisible est `null` : jamais
 * inventee, jamais remplacee par l heure de la requete.
 */
export interface GsBuildInfo {
  slot: GsDeploymentSlotState;
  /** SHA du commit construit, tel que le flux de construction l a pose (`GS_GIT_SHA`). */
  gitSha: string | null;
  /** Date de construction, ISO 8601 (`GS_BUILD_TIME`). */
  buildTime: string | null;
}

/**
 * Relatif, jamais `VITE_API_URL` : cette adresse est figee a la construction sur la
 * PRODUCTION, et une page servie par l emplacement interrogerait la production, qui
 * repondrait « production ». Seule l origine de la page sait quelle instance l a servie.
 */
const VERSION_ENDPOINT = '/api/version';

const INCONNU: GsBuildInfo = { slot: 'unknown', gitSha: null, buildTime: null };

function texteNonVide(valeur: unknown): string | null {
  return typeof valeur === 'string' && valeur.trim() !== '' ? valeur.trim() : null;
}

/** Lit le corps de `/api/version`. Tout ce qui n est pas reconnu devient `null` / `unknown`. */
export function parseBuildInfo(data: unknown): GsBuildInfo {
  if (!data || typeof data !== 'object') return INCONNU;
  const corps = data as { emplacement?: unknown; gitSha?: unknown; buildTime?: unknown };
  return {
    slot: corps.emplacement === 'preprod' || corps.emplacement === 'production' ? corps.emplacement : 'unknown',
    gitSha: texteNonVide(corps.gitSha),
    buildTime: texteNonVide(corps.buildTime),
  };
}

const FORME_DE_CLE_D_APP = /^[a-z][a-z0-9-]{0,39}$/;

/**
 * La cle de l app qui a servi la page (`appKey` de `/api/version` : `gest`, `admin`...), ou `null`.
 * Le bandeau de la copie la nomme (lot feat-la-copie-coupe-ses-sorties : « il va falloir que ca
 * soit clair sur quelle copie ca sort », David 2026-10-29). Une valeur hors forme ne s affiche pas.
 */
export function parseAppKey(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const cle = texteNonVide((data as { appKey?: unknown }).appKey)?.toLowerCase() ?? null;
  return cle && FORME_DE_CLE_D_APP.test(cle) ? cle : null;
}

interface Lecture {
  info: GsBuildInfo;
  appKey: string | null;
}

async function lire(fetchFn: typeof fetch): Promise<Lecture> {
  try {
    const res = await fetchFn(VERSION_ENDPOINT, { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return { info: INCONNU, appKey: null };
    const corps: unknown = await res.json();
    return { info: parseBuildInfo(corps), appKey: parseAppKey(corps) };
  } catch (err: unknown) {
    // Un affichage de version n est pas une fonction de l app : l echec se RETIENT comme
    // inconnu (etat, pas silence) et n empeche rien d autre de s afficher.
    void err;
    return { info: INCONNU, appKey: null };
  }
}

/**
 * Une lecture par page, PARTAGEE : le bandeau de preproduction et le pied du menu lisent la
 * meme reponse, donc la meme version, sans second appel. Le cache est tenu par la fonction
 * `fetch` elle-meme : en production elle ne change pas (une lecture), et un test qui remplace
 * `fetch` repart d une lecture neuve sans rien avoir a remettre a zero.
 */
const lectures = new WeakMap<typeof fetch, Promise<Lecture>>();

function lecturePartagee(): Promise<Lecture> {
  if (typeof fetch === 'undefined') return Promise.resolve({ info: INCONNU, appKey: null });
  const fetchFn = fetch;
  let lecture = lectures.get(fetchFn);
  if (!lecture) {
    lecture = lire(fetchFn);
    lectures.set(fetchFn, lecture);
  }
  return lecture;
}

export function lireBuildInfo(): Promise<GsBuildInfo> {
  return lecturePartagee().then((l) => l.info);
}

/** La cle de l app qui a servi la page, de la MEME lecture partagee (aucun second appel). */
export function lireAppKey(): Promise<string | null> {
  return lecturePartagee().then((l) => l.appKey);
}

/**
 * La construction qui sert la page, ou `null` tant que la lecture n est pas revenue.
 * Ne leve jamais : une panne rend `{ slot: 'unknown', gitSha: null, buildTime: null }`.
 */
export function useBuildInfo(): GsBuildInfo | null {
  const [info, setInfo] = useState<GsBuildInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    void lireBuildInfo().then((next) => {
      if (!cancelled) setInfo(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return info;
}

/**
 * Le SHA court (7 caracteres) d une construction, ou `dev` quand le SHA n est pas celui d un
 * commit (`local` en poste de developpement, valeur inconnue). `null` quand le serveur n en
 * dit rien : on n affiche pas une version que personne n a donnee.
 */
export function shortSha(gitSha: string | null): string | null {
  if (!gitSha) return null;
  return /^[0-9a-f]{7,40}$/i.test(gitSha) ? gitSha.slice(0, 7).toLowerCase() : 'dev';
}

/** `AAAA-MM-JJ HH:mm UTC`, ou `null` si la date est absente ou illisible. */
export function formatBuildDate(buildTime: string | null): string | null {
  if (!buildTime) return null;
  const date = new Date(buildTime);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

/**
 * La FORME unique de la version, rendue a l identique en production (pied du menu) et en
 * preproduction (bandeau) : `a1b2c3d · 2026-09-30 14:05 UTC`. Fonction pure, donc les deux
 * cotes rendent la meme chaine pour la meme reponse de `/api/version`.
 */
export function formatBuildStamp(info: Pick<GsBuildInfo, 'gitSha' | 'buildTime'>): string | null {
  const sha = shortSha(info.gitSha);
  if (!sha) return null;
  // Un SHA qui n en est pas un (`local`) n a pas de date de construction qui vaille.
  const date = sha === 'dev' ? null : formatBuildDate(info.buildTime);
  return date ? `${sha} · ${date}` : sha;
}
